import React, { useCallback, useEffect } from 'react'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import MessageCard from '@groovehq/internal-design-system/lib/components/MessageCard/MessageCard'
import {
  paragraph,
  text,
} from '@groovehq/internal-design-system/lib/styles/elements'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'
import headerImg from '@groovehq/internal-design-system/lib/assets/images/forward-send.png'
import { useDrawer } from 'ducks/drawers/hooks'
import {
  DRAWER_TYPE_CHANNELS_CREATION_FEEDBACK,
  DRAWER_TYPE_CHANNELS_FORWARD_DETAILS,
  DRAWER_TYPE_CHANNELS_FORWRADING_INSTRUCTIONS,
} from 'ducks/drawers/types'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { useChannel } from 'ducks/channels/hooks'

import { styles } from './styles'

const EmailForward = ({
  drawerResourceId: channelId,
  drawerChannelType: channelType = 'forwarding',
  drawerSource: source = 'settings',
  onClose,
  previousDrawer,
}) => {
  const { channel, isLoading, isMissing } = useChannel(channelId)
  useEffect(
    () => {
      if (isMissing) onClose()
    },
    [isMissing, onClose]
  )
  const { name: channelName, email: channelEmail } = channel || {}
  const channelTitle = channelEmail || channelName
  const isPartOfWizard = !!previousDrawer
  const canSetupLater = source !== 'inbox' && isPartOfWizard
  const { drawerId: setupDrawerId, openDrawer: openSetupDrawer } = useDrawer({
    id: null,
    type: DRAWER_TYPE_CHANNELS_FORWARD_DETAILS,
    closeIgnoresStack: true,
  })

  const {
    drawerId: checkInstructionsDrawerId,
    openDrawer: openCheckInstructionsDrawer,
  } = useDrawer({
    id: null,
    type: DRAWER_TYPE_CHANNELS_FORWRADING_INSTRUCTIONS,
    closeIgnoresStack: false,
  })

  const {
    drawerId: setupLaterDrawerId,
    openDrawer: openSetupLaterDrawer,
  } = useDrawer({
    id: null,
    type: DRAWER_TYPE_CHANNELS_CREATION_FEEDBACK,
    closeIgnoresStack: true,
  })

  const handleSetup = useCallback(
    () => {
      openSetupDrawer(channelId, {
        query: {
          ...buildDrawerQueryParam(
            setupDrawerId,
            'drawerChannelType',
            channelType
          ),
        },
      })
    },
    [openSetupDrawer, channelId, setupDrawerId, channelType]
  )

  const handleCheckInstructions = useCallback(
    () => {
      openCheckInstructionsDrawer(channelId, {
        query: {
          ...buildDrawerQueryParam(
            checkInstructionsDrawerId,
            'drawerChannelType',
            channelType
          ),
        },
      })
    },
    [
      openCheckInstructionsDrawer,
      channelId,
      checkInstructionsDrawerId,
      channelType,
    ]
  )

  const handleSetupLater = useCallback(
    () => {
      openSetupLaterDrawer(channelId, {
        query: {
          ...buildDrawerQueryParam(
            setupLaterDrawerId,
            'drawerSetupIncomplete',
            1
          ),
          ...buildDrawerQueryParam(
            setupLaterDrawerId,
            'drawerChannelType',
            channelType
          ),
        },
      })
    },
    [openSetupLaterDrawer, channelId, setupLaterDrawerId, channelType]
  )

  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: !isPartOfWizard })
    },
    [onClose, isPartOfWizard]
  )
  const modalCloseButtonText = isPartOfWizard ? 'Back' : 'Cancel'

  const drawerFooter = (
    <ModalBtns
      tertiaryBtnText={modalCloseButtonText}
      onClickTertiaryBtn={handleBack}
    />
  )

  return (
    <>
      <Drawer
        title={'Forwarding setup'}
        footer={drawerFooter}
        onClose={onClose}
        isLoading={isLoading}
      >
        <HeaderWithImage
          className="grui mt-10"
          imgWidth="120px"
          css={styles.header}
          img={headerImg}
          alt="Email forwarding"
          title="Forward your email to Groove"
          intro={`To start receiving emails from your ${channelTitle} mailbox in Groove, you will need to forward your incoming emails across from your current email tool.`}
        />

        {isPartOfWizard && (
          <MessageCard type="warning" className="grui mt-10" closeable={false}>
            <div css={text.styles.fontMedium}>Heads up!</div>
            <p css={paragraph.styles.preflight} className="grui my-5">
              You will not receive emails to this mailbox in Groove until email
              forwarding is setup.
            </p>
          </MessageCard>
        )}
        <div css={styles.controls} className="grui mt-12">
          <Button onClick={handleSetup}>Setup email forwarding now</Button>
          <Button type="secondary" onClick={handleCheckInstructions}>
            Email forwarding instructions to a colleague
          </Button>
          {canSetupLater && (
            <Button type="tertiary" onClick={handleSetupLater}>
              I&rsquo;ll setup forwarding later
            </Button>
          )}
        </div>
      </Drawer>
    </>
  )
}

export default EmailForward
